
import React, { useState } from "react";
import {
  Box, Button, Checkbox, Chip, CircularProgress,
  Grid, MenuItem, Pagination, Stack,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography
} from "@mui/material";
import { Link } from "react-router-dom";
import { fetchWithAuth } from "utils/fetchWithAuth";

const API_URL = process.env.REACT_APP_API_URL;
const rowsPerPage = 15;

function formatDate(date) {
  if (!date) return "en cours";
  try {
    return new Date(date).toLocaleDateString("fr-FR");
  } catch {
    return date;
  }
}

function sortRows(rows, orderBy, order) {
  if (!orderBy) return rows;

  return [...rows].sort((a, b) => {
    let va, vb;

    switch (orderBy) {
      case "lot":
        va = a.locations?.[0]?.lot_nom || ""; vb = b.locations?.[0]?.lot_nom || ""; break;
      case "adresse":
        va = a.locations?.[0]?.lot_adresse || ""; vb = b.locations?.[0]?.lot_adresse || ""; break;
      case "entree":
        va = a.locations?.[0]?.date_entree || ""; vb = b.locations?.[0]?.date_entree || ""; break;
      case "sortie":
        va = a.locations?.[0]?.date_sortie || ""; vb = b.locations?.[0]?.date_sortie || ""; break;
      case "dette":
        va = a.locations?.[0]?.dette || 0; vb = b.locations?.[0]?.dette || 0; break;
      default:
        va = a.nom || ""; vb = b.nom || "";
    }

    if (va < vb) return order === "asc" ? -1 : 1;
    if (va > vb) return order === "asc" ? 1 : -1;
    return 0;
  });
}

export default function RechercheLocataires() {
  const [filters, setFilters] = useState({});
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState("asc");   

  const handleInputChange = (e) =>
    setFilters({ ...filters, [e.target.name]: e.target.value });

  const search = async () => {
    setLoading(true);
    const params = { ...filters };
    Object.keys(params).forEach(k => (!params[k] && params[k] !== 0) && delete params[k]);
    const searchParams = new URLSearchParams(params).toString();
    const url = `${API_URL}/api/locataires/search/?${searchParams}`;
    const res = await fetchWithAuth(url);
    const result = await res.json();
    setData(result);
    setCount(result.length); 
    setPage(1);
    setLoading(false);
  };

  const handleSubmit = e => {
    e.preventDefault();
    setPage(1);
    search(1);
  };

  const handleClear = () => {
    setFilters({});
    setPage(1);
    setCount(0);
    setData([]);
    setOrderBy(null);
    setOrder("asc");
  };

  const handleChangePage = (_, value) => {
    setPage(value);
  };

  const handleSort = (column) => {
  if (orderBy === column) {
    setOrder(order === "asc" ? "desc" : "asc");
  } else {
    setOrderBy(column);
    setOrder("asc");
  }
}
  const sortedData = sortRows(data, orderBy, order);
  const paginatedData = sortedData.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, idx) => currentYear - idx);

  return (
    <Box>
      <Box component="form" onSubmit={handleSubmit} sx={{ borderRadius: 2, p: 2 }}>
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField name="nom" fullWidth value={filters.nom || ""} onChange={handleInputChange} label="Nom" size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="prenom" fullWidth value={filters.prenom || ""} onChange={handleInputChange} label="Prénom" size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="code_postal" fullWidth value={filters.code_postal || ""} onChange={handleInputChange} label="Code Postal" size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="ville" fullWidth value={filters.ville || ""} onChange={handleInputChange} label="Ville" size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="telephone" fullWidth value={filters.ville || ""} onChange={handleInputChange} label="Téléphone" size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField name="email" fullWidth value={filters.ville || ""} onChange={handleInputChange} label="Mail" size="small" />
            </Grid>
          </Grid>
          <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Grid item xs={12} sm={3} md={2}>
              <Typography variant="body2" color="text.secondary">
                Année de location :
              </Typography>
            </Grid>
            <Grid item>
              <TextField
                select
                name="annee_location"
                value={filters.annee_location || ""}
                onChange={handleInputChange}
                label=""
                size="small"
                sx={{ minWidth: 120 }}
              >
                <MenuItem value="">--</MenuItem>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item>
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
                sx={{ ml: 3, verticalAlign: "middle" }}
              >
                Uniquement avec dette en cours
              </Typography>
              <Checkbox
                name="dette"
                checked={filters.dette === "1"}
                onChange={(e) =>
                  handleInputChange({
                    target: {
                      name: "dette",
                      value: e.target.checked ? "1" : "",
                    },
                  })
                }
              />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button type="submit" variant="contained" color="primary">{loading ? <CircularProgress size={18} /> : "Rechercher"}</Button>
            <Button type="button" variant="outlined" color="secondary" onClick={handleClear}>Effacer filtres</Button>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ border: "1px solid #eee", borderRadius: 2, p: 1, mt: 2, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#f7fafb" }}>
              <TableCell sx={{ fontWeight: 700, cursor: "pointer" }} onClick={() => handleSort("locataire")}>
                Locataire {orderBy === "locataire" ? (order === "asc" ? "▲" : "▼") : ""}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, cursor: "pointer" }} onClick={() => handleSort("lot")}>
                Lot {orderBy === "lot" ? (order === "asc" ? "▲" : "▼") : ""}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, cursor: "pointer" }} onClick={() => handleSort("adresse")}>
                Adresse {orderBy === "adresse" ? (order === "asc" ? "▲" : "▼") : ""}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, cursor: "pointer" }} onClick={() => handleSort("entree")}>
                Date entrée {orderBy === "entree" ? (order === "asc" ? "▲" : "▼") : ""}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, cursor: "pointer" }} onClick={() => handleSort("sortie")}>
                Date sortie {orderBy === "sortie" ? (order === "asc" ? "▲" : "▼") : ""}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, cursor: "pointer" }} onClick={() => handleSort("dette")}>
                Dette {orderBy === "dette" ? (order === "asc" ? "▲" : "▼") : ""}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ fontStyle: "italic" }}>
                  Aucun locataire trouvé
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((locataire) =>
                (locataire.locations?.length > 0 ? locataire.locations : [null]).map((location, idx) => (
                  <TableRow key={locataire.id + (location ? "_" + location.id : "")}>
                    {/* 1 cellule fusionnée pour locataire (nom/prénom), une fois par bloc de locations */}
                    {idx === 0 && (
                      <TableCell rowSpan={locataire.locations?.length || 1}>
                        <Link to={`/locataires/${locataire.id}`} style={{ textDecoration: "none", color: "#1565c0", fontWeight: 500 }}>
                          {locataire.nom} {locataire.prenom}
                        </Link>
                      </TableCell>
                    )}
                    {location ? (
                      <>
                        <TableCell>
                          <Link to={`/lots/${location.lot_id}`} style={{ textDecoration: "none", color: "#1565c0", fontWeight: 500 }}>
                            {location.lot_nom}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Link to={`/biens/${location.lot_bien_id}`} style={{ textDecoration: "none", color: "#1565c0", fontWeight: 500 }}>
                            {location.lot_adresse}
                          </Link>
                        </TableCell>
                        <TableCell>{formatDate(location.date_entree)}</TableCell>
                        <TableCell>{formatDate(location.date_sortie)}</TableCell>
                        <TableCell>
                          <Chip
                            label={location.dette?.toLocaleString("fr-FR", { style: "currency", currency: "EUR" }) || "0 €"}
                            color={location.dette > 0 ? "error" : "success"}
                            size="small"
                          />
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell colSpan={5} align="center" sx={{ fontStyle: "italic" }}>
                          Pas de location
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              )
            )}
          </TableBody>
        </Table>
        {count > rowsPerPage && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              count={Math.ceil(count / rowsPerPage)}
              page={page}
              onChange={handleChangePage}
              size="small"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}